import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiImageAdd, BiTrash } from 'react-icons/bi'
import { URL as URL2 } from '../../api'

const DragDropFiles = ({ formData, setFormData, name, selectedItem, title }) => {
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)
    const { t } = useTranslation()

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files)
        addNewFiles(selectedFiles)
    }

    const addNewFiles = (newFiles) => {
        setFormData(prevData => {
            const updatedFiles = [...(prevData[name] || [])];
            const updatedTitles = [...(prevData.fileTitles || [])];

            newFiles.forEach(file => {
                const isImage = file.type.match('image.*');
                const isPdf = file.type === 'application/pdf';
                if (isImage || isPdf) {
                    const fileWithPreview = {
                        file,
                        preview: isImage ? URL.createObjectURL(file) : null,
                        name: file.name,
                        size: file.size,
                        id: `${file.name}-${Date.now()}`
                    }
                    updatedFiles.push(fileWithPreview);
                    updatedTitles.push({ tr: "", en: "" });
                }
            });

            return {
                ...prevData,
                [name]: updatedFiles,
                fileTitles: updatedTitles
            };
        });
    }

    const handleRemoveFile = (fileId, index) => {
        setFormData(prevData => {
            if (!prevData[name] || !Array.isArray(prevData[name])) {
                return prevData;
            }

            const fileToRemove = prevData[name][index];
            if (fileToRemove && typeof fileToRemove === 'object' && fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }

            const updatedFiles = prevData[name].filter((_, i) => i !== index);
            const updatedDescriptions = (prevData.fileTitles || []).filter((_, i) => i !== index);

            return {
                ...prevData,
                [name]: updatedFiles,
                fileTitles: updatedDescriptions
            };
        });
    }

    const handleDragEnter = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isDragging) setIsDragging(true)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFiles = Array.from(e.dataTransfer.files)
            addNewFiles(droppedFiles)
            e.dataTransfer.clearData()
        }
    }

    const openFileSelector = () => {
        fileInputRef.current.click()
    }

    return (
        <div className="mb-4">
            <label className="form-label">{title ? title : t("images")}</label>
            <div
                className={`image-upload-area ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileSelector}
                style={{
                    border: '2px dashed #ccc',
                    borderRadius: '6px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragging ? '#f8f9fa' : 'transparent',
                    minHeight: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*,application/pdf"
                />
                <div>
                    <BiImageAdd size={32} style={{ color: '#6c757d', marginBottom: '10px' }} />
                    <p className="mb-0">{t("drag_drop_images")}</p>
                    <p className="text-muted small">{t("or_click_to_select")}</p>
                </div>
            </div>

            {formData[name]?.length > 0 && (
                <div className="d-flex flex-wrap mt-3">
                    {formData[name]?.map((file, index) => {
                        const isPdf = file?.name?.toLowerCase().endsWith('.pdf') || file?.file?.toLowerCase?.().endsWith('.pdf');
                        return (
                            <div key={typeof file === 'object' ? file?.id : `existing-file-${index}`}>
                                <div className="position-relative mb-2" style={{ width: 150, height: 100, overflow: 'hidden', borderRadius: '4px', marginRight: 10, }}>
                                    {isPdf ? (
                                        <div style={{ width: 150, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', border: '1px solid #ddd' }}>
                                            <span style={{ fontWeight: 'bold', color: '#b71c1c' }}>PDF</span>
                                            <span style={{ marginLeft: 8 }}>{file?.name}</span>
                                        </div>
                                    ) : (
                                        <img
                                            src={
                                                file?.preview
                                                    ? file.preview
                                                    : typeof file.file === 'string'
                                                        ? `${URL2}${file.file}`
                                                        : ''
                                            }
                                            alt={file?.name || `Image ${index + 1}`}
                                            className="img-thumbnail"
                                            style={{ width: 150, height: 100, objectFit: 'contain' }}
                                        />
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger position-absolute"
                                        onClick={() => handleRemoveFile(file?.id, index)}
                                        style={{ top: '5px', right: '5px', padding: '2px 5px', opacity: 0.8 }}
                                    >
                                        <BiTrash size={16} />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default DragDropFiles
